import { motion } from 'framer-motion';
import Image from 'next/image';
import { Trans, useTranslation } from 'next-i18next';

import Header from '@/common/header';
import { FaqAccordion } from '@/components/trial/shared/faq-accordion';
import { PingCounter } from '@/components/trial/shared/ping-counter';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';
import { outfit } from '@/public/assets/fonts/Outfit';

import CreateAccountV2 from '../../create-account-v2';
import { Footer } from '../../footer';
import { NoPingAppVideo } from '../../shared/noping-app-video';
import type { DefaultTrialProps } from '../../trial-layout-switcher';
import { GoldenSnitches, HarryPotterBg, LordOfTheRingsBg, MatrixBg, SpeedBg, StarWarsBg } from './components/backgrounds';
import { ACCENT_BLUE, CARD_DARK, DARK_BG, EASE_OUT, EVIL_RED, GAME_IMAGES, GOLD, MUTED_GRAY, NOPING_GREEN, NOPING_STATS, POP_TOOLS, PRO_ENDORSEMENTS, TEXT_LIGHT } from './components/constants';
import { FlickeringStat, HyperspaceRouting, MatrixPill, MultiInternetLiveDemo } from './components/interactive';
import { Section, SectionTitle, ThemedCard } from './components/ui-helpers';

interface JoinTheRebellionProps extends DefaultTrialProps {}

export function JoinTheRebellion({ daysTrial, trialConfigId, trialPageId, isDisabledButton = false }: JoinTheRebellionProps) {
  const { t } = useTranslation(['components/trial/lp/v4d']);

  const faqItems = Array.from({ length: 6 }, (_, i) => ({
    q: t(`faq.items.${i}.q`),
    a: t(`faq.items.${i}.a`),
  }));

  return (
    <div className={`${bai_jamjuree.className} ${outfit.variable} relative min-h-screen`} style={{ background: DARK_BG, color: TEXT_LIGHT }}>
      <Header variant='primary' />

      {/* SECTIONS 1+2 WRAPPER -- Unified Matrix background */}
      <div className='relative' style={{ zIndex: 1 }}>
        <MatrixBg />

        {/* SECTION 1 -- HERO */}
        <section id='hero' className='relative flex min-h-screen items-center px-4 pt-20 md:px-8' style={{ zIndex: 1 }}>
          <div className='relative mx-auto grid w-full max-w-7xl grid-cols-1 items-stretch gap-8 lg:grid-cols-2 lg:gap-12' style={{ zIndex: 1 }}>
            <div>
              <motion.p className='mb-3 text-sm font-semibold uppercase tracking-[0.3em]' style={{ color: GOLD, fontFamily: 'var(--font-outfit)' }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1, ease: EASE_OUT }}>
                {t('hero.subtitle')}
              </motion.p>

              <motion.h1 className='mb-3 text-3xl font-bold leading-tight md:text-4xl lg:text-5xl' style={{ fontFamily: 'var(--font-bai)', background: `linear-gradient(135deg, ${GOLD}, ${NOPING_GREEN})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2, ease: EASE_OUT }}>
                {t('hero.title')}
              </motion.h1>

              <motion.p className='mb-3 max-w-lg text-base leading-relaxed md:text-lg' style={{ color: '#C8C8CE', fontFamily: 'var(--font-outfit)' }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.35, ease: EASE_OUT }}>
                {t('hero.description')}
              </motion.p>

              <motion.div className='mb-3' initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.5, ease: EASE_OUT }}>
                <PingCounter startPing={187} endPing={5} duration={2.5} accentColor={NOPING_GREEN} size='lg' labelOptimizing={t('ping.optimizing')} labelDone={t('ping.done')} />
              </motion.div>

              <motion.div className='relative overflow-hidden rounded-xl border' style={{ borderColor: `${NOPING_GREEN}25`, boxShadow: `0 0 40px ${NOPING_GREEN}10` }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.65, ease: EASE_OUT }}>
                <NoPingAppVideo />
              </motion.div>
            </div>

            {/* RIGHT: CTA Card */}
            <motion.div id='hero-form' className='relative flex flex-col pb-6' initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.3, ease: EASE_OUT }}>
              <ThemedCard className='flex flex-1 flex-col justify-center p-6 md:p-8' glow={NOPING_GREEN} opaque>
                <h2 className='mb-2 text-center text-2xl font-bold md:text-3xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }}>
                  {t('hero.form_title')}
                </h2>
                <p className='mb-5 text-center text-sm' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                  {t('hero.form_subtitle')}
                </p>

                <CreateAccountV2 daysTrial={daysTrial} customTrialData={{ custom: trialConfigId, trialPageId }} isDisabledButton={isDisabledButton} formId='v4d-hero-form' accentColor={NOPING_GREEN} />

                <div className='mt-4 flex items-center justify-center gap-5 pt-4' style={{ borderTop: `1px solid ${ACCENT_BLUE}15` }}>
                  <span className='text-[10px] uppercase tracking-wider' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                    {t('hero.certified_by')}
                  </span>
                  <div className='flex items-center gap-1.5'>
                    <svg width='16' height='16' viewBox='0 0 21 21' className='shrink-0'>
                      <rect x='1' y='1' width='9' height='9' fill='#F25022' />
                      <rect x='11' y='1' width='9' height='9' fill='#7FBA00' />
                      <rect x='1' y='11' width='9' height='9' fill='#00A4EF' />
                      <rect x='11' y='11' width='9' height='9' fill='#FFB900' />
                    </svg>
                    <span className='text-[10px] font-semibold' style={{ color: '#A0A0B0', fontFamily: 'var(--font-outfit)' }}>
                      {t('hero.microsoft_certified')}
                    </span>
                  </div>
                  <div className='flex items-center gap-1.5'>
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='none' className='shrink-0'>
                      <path d='M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z' fill='#00A4EF' opacity='0.2' />
                      <path d='M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z' stroke='#00A4EF' strokeWidth='1.5' fill='none' />
                      <path d='M9 12l2 2 4-4' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    </svg>
                    <span className='text-[10px] font-semibold' style={{ color: '#A0A0B0', fontFamily: 'var(--font-outfit)' }}>
                      {t('hero.digicert_secured')}
                    </span>
                  </div>
                </div>
              </ThemedCard>

              <div className='mt-4 flex flex-wrap justify-center gap-2 px-4 md:absolute md:inset-x-0 md:-bottom-0 md:z-20 md:mt-0 md:gap-3'>
                {[
                  { value: '80%', label: t('hero.badge_ping') },
                  { value: '110%', label: t('hero.badge_fps') },
                  { value: '6+', label: t('hero.badge_multi_net') },
                  { value: '5+', label: t('hero.badge_multiconn') },
                ].map((b, i) => (
                  <div key={i} className='flex items-center gap-1.5 rounded-full border px-3 py-1.5 shadow-lg' style={{ backgroundColor: '#0c0e14', borderColor: `${NOPING_GREEN}40`, boxShadow: `0 0 12px ${NOPING_GREEN}15` }}>
                    <span className='text-xs font-bold' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-bai)' }}>
                      {b.value}
                    </span>
                    <span className='text-[9px] uppercase tracking-wider' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                      {b.label}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2 -- "The Red Pill or The Green Pill" */}
        <Section id='red-pill'>
          <SectionTitle subtitle={t('red_pill.subtitle')} title={t('red_pill.title')} description={t('red_pill.description')} accentColor={ACCENT_BLUE} />

          <div className='grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6'>
            <ThemedCard className='p-5 md:p-7' glow={EVIL_RED} delay={0.1}>
              <div className='mb-4 text-center'>
                <MatrixPill color={EVIL_RED} glowColor='#FF3B3B' />
                <h3 className='text-xl font-bold md:text-2xl' style={{ color: EVIL_RED, fontFamily: 'var(--font-bai)' }}>
                  {t('red_pill.red_title')}
                </h3>
                <p className='mt-1 text-sm' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                  {t('red_pill.red_desc')}
                </p>
              </div>
              <div className='space-y-4'>
                <FlickeringStat baseValue={180} range={40} suffix='ms' color={EVIL_RED} label={t('red_pill.label_ping')} />
                <FlickeringStat baseValue={12} range={8} suffix='%' color={EVIL_RED} label={t('red_pill.label_packet_loss')} />
                <FlickeringStat baseValue={45} range={20} suffix='ms' color={EVIL_RED} label={t('red_pill.label_jitter')} />
              </div>
              <p className='mt-4 rounded-full px-4 py-2.5 text-center text-sm font-semibold tracking-wide' style={{ backgroundColor: `${EVIL_RED}12`, border: `1px solid ${EVIL_RED}25`, color: EVIL_RED, fontFamily: 'var(--font-outfit)' }}>
                {t('red_pill.red_result')}
              </p>
            </ThemedCard>

            <ThemedCard className='p-5 md:p-7' glow={NOPING_GREEN} delay={0.2}>
              <div className='mb-4 text-center'>
                <MatrixPill color={NOPING_GREEN} glowColor='#93FF18' />
                <h3 className='text-xl font-bold md:text-2xl' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-bai)' }}>
                  {t('red_pill.green_title')}
                </h3>
                <p className='mt-1 text-sm' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                  {t('red_pill.green_desc')}
                </p>
              </div>
              <div className='space-y-4'>
                <FlickeringStat baseValue={3} range={4} suffix='ms' color={NOPING_GREEN} label={t('red_pill.label_ping')} flicker={false} />
                <FlickeringStat baseValue={0} range={1} suffix='%' color={NOPING_GREEN} label={t('red_pill.label_packet_loss')} flicker={false} />
                <FlickeringStat baseValue={2} range={2} suffix='ms' color={NOPING_GREEN} label={t('red_pill.label_jitter')} flicker={false} />
              </div>
              <p className='mt-4 rounded-full px-4 py-2.5 text-center text-sm font-semibold tracking-wide' style={{ backgroundColor: `${NOPING_GREEN}12`, border: `1px solid ${NOPING_GREEN}25`, color: NOPING_GREEN, fontFamily: 'var(--font-outfit)' }}>
                {t('red_pill.green_result')}
              </p>
            </ThemedCard>
          </div>

          <motion.p className='mt-6 text-center text-sm italic md:text-base' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.6, ease: EASE_OUT }} viewport={{ once: true }}>
            <Trans i18nKey='red_pill.quote' ns='components/trial/lp/v4d' components={{ 1: <br />, 2: <span style={{ color: NOPING_GREEN }} /> }} />
          </motion.p>
        </Section>
      </div>

      {/* SECTION 3 -- "Entering Hyperspace" */}
      <Section id='hyperspace' bg={<StarWarsBg />}>
        <SectionTitle subtitle={t('hyperspace.subtitle')} title={t('hyperspace.title')} description={t('hyperspace.description')} accentColor={ACCENT_BLUE} />
        <HyperspaceRouting />

        <div className='mt-6 grid grid-cols-1 gap-4 md:grid-cols-3'>
          {Array.from({ length: 3 }, (_, i) => (
            <ThemedCard key={i} className='p-4' glow={ACCENT_BLUE} delay={0.08 * i}>
              <span className='mb-2 block text-2xl'>{['\uD83D\uDE80', '\uD83D\uDCE1', '\uD83D\uDEE1\uFE0F'][i]}</span>
              <h3 className='mb-1 text-sm font-bold' style={{ color: TEXT_LIGHT, fontFamily: 'var(--font-bai)' }}>
                {t(`hyperspace.cards.${i}.title`)}
              </h3>
              <p className='text-xs' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                {t(`hyperspace.cards.${i}.desc`)}
              </p>
            </ThemedCard>
          ))}
        </div>
      </Section>

      {/* SECTION 4 -- "The Multiverse of Connections" */}
      <Section id='multiverse' bg={<StarWarsBg />}>
        <SectionTitle subtitle={t('multiverse.subtitle')} title={t('multiverse.title')} description={t('multiverse.description')} accentColor={ACCENT_BLUE} />
        <MultiInternetLiveDemo />
      </Section>

      {/* SECTION 5 -- "Power-Ups Unlocked" */}
      <Section
        id='power-ups'
        bg={
          <>
            <HarryPotterBg />
            <GoldenSnitches />
          </>
        }
      >
        <SectionTitle subtitle={t('power_ups.subtitle')} title={t('power_ups.title')} description={t('power_ups.description')} accentColor={NOPING_GREEN} />

        <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'>
          {POP_TOOLS.map((tool, i) => (
            <ThemedCard key={i} className='p-5' glow={NOPING_GREEN} delay={0.08 * i}>
              <div className='flex items-start gap-4'>
                <div className='flex size-12 shrink-0 items-center justify-center rounded-lg text-2xl' style={{ backgroundColor: `${NOPING_GREEN}10`, border: `1px solid ${NOPING_GREEN}25` }}>
                  {tool.icon}
                </div>
                <div>
                  <h3 className='mb-1 text-base font-bold' style={{ color: TEXT_LIGHT, fontFamily: 'var(--font-bai)' }}>
                    {t(`power_ups.tools.${i}.pop_name`)}
                  </h3>
                  <p className='mb-1 text-xs' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-outfit)' }}>
                    {tool.name}
                  </p>
                  <p className='text-sm' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                    {t(`power_ups.tools.${i}.description`)}
                  </p>
                </div>
              </div>
            </ThemedCard>
          ))}
        </div>

        <motion.div className='mt-10 text-center' initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5, ease: EASE_OUT }} viewport={{ once: true }}>
          <p className='mb-4 text-sm italic' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
            {t('power_ups.quote')}
          </p>
        </motion.div>
      </Section>

      {/* SECTION 6 -- "Hall of Legends" */}
      <Section id='legends' bg={<HarryPotterBg />}>
        <SectionTitle subtitle={t('legends.subtitle')} title={t('legends.title')} description={t('legends.description')} accentColor={GOLD} />

        <style>{`
          @keyframes scroll-players-jtr { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
          .jtr-player-carousel-track { animation: scroll-players-jtr 35s linear infinite; }
          .jtr-player-carousel-track:hover { animation-play-state: paused; }
          /* transform is physical — CSS never mirrors it for writing direction. These tracks are
             width: max-content inside a narrower clip box, so under the dir="rtl" that /sa inherits
             from <html> the over-constrained box anchors to the RIGHT edge and overflows left, and the
             negative shift drags the strip out of the clip box instead of through it. Mirrored keyframes
             shift positive; only animation-name is overridden. */
          @keyframes scroll-players-jtr-rtl { 0% { transform: translateX(0); } 100% { transform: translateX(50%); } }
          [dir='rtl'] .jtr-player-carousel-track { animation-name: scroll-players-jtr-rtl; }
        `}</style>
        <div className='relative overflow-hidden' style={{ maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)', WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)' }}>
          <div className='jtr-player-carousel-track flex gap-5' style={{ width: 'max-content' }}>
            {[...PRO_ENDORSEMENTS, ...PRO_ENDORSEMENTS].map((endorsement, i) => (
              <div key={`${endorsement.name}-${i}`} className='w-[320px] shrink-0 rounded-xl border p-5 backdrop-blur-sm' style={{ backgroundColor: `${GOLD}06`, borderColor: `${GOLD}30`, boxShadow: `0 0 30px ${GOLD}10, inset 0 1px 0 ${GOLD}15` }}>
                <div className='mb-3 flex items-center gap-3'>
                  <div className='size-12 shrink-0 overflow-hidden rounded-full border-2' style={{ borderColor: `${GOLD}40` }}>
                    <Image src={endorsement.image} alt={endorsement.name} width={48} height={48} className='size-full object-cover' />
                  </div>
                  <div>
                    <h3 className='text-base font-bold' style={{ color: TEXT_LIGHT, fontFamily: 'var(--font-bai)' }}>
                      {endorsement.name}
                    </h3>
                    <p className='text-xs' style={{ color: GOLD, fontFamily: 'var(--font-outfit)' }}>
                      {t(`pros.items.${i % PRO_ENDORSEMENTS.length}.title`)}
                    </p>
                    <p className='text-xs' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                      {t(`pros.items.${i % PRO_ENDORSEMENTS.length}.game`)}
                    </p>
                  </div>
                </div>
                <p className='text-sm italic leading-relaxed' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                  &quot;{t(`pros.items.${i % PRO_ENDORSEMENTS.length}.quote`)}&quot;
                </p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* SECTIONS 7+8 WRAPPER -- Unified Lord of the Rings background */}
      <div className='relative' style={{ zIndex: 1 }}>
        <LordOfTheRingsBg />

        {/* SECTION 7 -- "Compatible Realms" */}
        <section id='realms' className='relative px-4 py-10 md:px-8 md:py-14' style={{ zIndex: 1 }}>
          <div className='relative mx-auto max-w-6xl' style={{ zIndex: 1 }}>
            <SectionTitle subtitle={t('realms.subtitle')} title={t('realms.title')} description={t('realms.description', { total: NOPING_STATS.gamesSupported })} accentColor={ACCENT_BLUE} />

            <style>{`
              @keyframes scroll-games-jtr { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
              .jtr-game-carousel-track { animation: scroll-games-jtr 25s linear infinite; }
              .jtr-game-carousel-track:hover { animation-play-state: paused; }
              @keyframes scroll-games-jtr-rtl { 0% { transform: translateX(0); } 100% { transform: translateX(50%); } }
              [dir='rtl'] .jtr-game-carousel-track { animation-name: scroll-games-jtr-rtl; }
            `}</style>
            <div className='relative overflow-hidden' style={{ maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)', WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)' }}>
              <div className='jtr-game-carousel-track flex gap-5' style={{ width: 'max-content' }}>
                {[...GAME_IMAGES, ...GAME_IMAGES].map((game, i) => (
                  <div key={`${game.name}-${i}`} className='flex w-[130px] shrink-0 cursor-pointer flex-col items-center gap-2 transition-transform duration-300 hover:scale-105'>
                    <div className='size-[90px] overflow-hidden rounded-xl border' style={{ borderColor: `${ACCENT_BLUE}20`, backgroundColor: CARD_DARK }}>
                      <Image src={`/images/${game.file}`} alt={game.name} width={90} height={90} className='h-auto w-full object-cover' />
                    </div>
                    <span className='text-center text-xs font-semibold leading-tight' style={{ color: TEXT_LIGHT, fontFamily: 'var(--font-outfit)' }}>
                      {game.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <motion.p className='mt-6 text-center text-sm' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.5, ease: EASE_OUT }} viewport={{ once: true }}>
              {t('realms.more_games', { total: NOPING_STATS.gamesSupported })}
            </motion.p>
          </div>
        </section>

        {/* SECTION 8 -- FAQ */}
        <section id='faq' className='relative px-4 py-14 md:px-8 md:py-20' style={{ zIndex: 1 }}>
          <div className='relative mx-auto max-w-6xl' style={{ zIndex: 1 }}>
            <SectionTitle subtitle={t('faq.subtitle')} title={t('faq.title')} description={t('faq.description')} accentColor={GOLD} />
            <div className='mx-auto max-w-3xl'>
              <FaqAccordion items={faqItems} accentColor={GOLD} />
            </div>
          </div>
        </section>
      </div>

      {/* SECTION 9 -- Final CTA */}
      <section id='final-cta' className='relative flex min-h-screen items-center overflow-hidden px-4 md:px-8' style={{ zIndex: 1 }}>
        <SpeedBg />
        <div className='pointer-events-none absolute inset-0' style={{ background: `radial-gradient(ellipse 50% 40% at 30% 50%, ${NOPING_GREEN}02, transparent), radial-gradient(ellipse 50% 50% at 70% 50%, ${ACCENT_BLUE}02, transparent)` }} />
        <motion.div className='absolute inset-x-0 top-0 h-px' style={{ background: `linear-gradient(90deg, transparent, ${NOPING_GREEN}20, ${ACCENT_BLUE}20, transparent)` }} animate={{ opacity: [0.2, 0.5, 0.2] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' as const }} />

        <div className='relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 py-16 lg:grid-cols-2 lg:gap-16' style={{ zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, ease: EASE_OUT }} viewport={{ once: true }}>
            <div className='mb-5 inline-flex items-center gap-2 rounded-full border px-4 py-1.5' style={{ borderColor: `${NOPING_GREEN}30`, backgroundColor: `${NOPING_GREEN}08` }}>
              <motion.div className='size-2 rounded-full' style={{ backgroundColor: NOPING_GREEN }} animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' as const }} />
              <span className='text-xs font-semibold uppercase tracking-wider' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-outfit)' }}>
                {t('final_cta.badge')}
              </span>
            </div>

            <h2 className='mb-4 text-3xl font-bold leading-tight md:text-4xl lg:text-5xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }}>
              <Trans i18nKey='final_cta.title' ns='components/trial/lp/v4d' components={{ 1: <br />, 2: <span style={{ color: NOPING_GREEN }} /> }} />
            </h2>

            <p className='mb-6 max-w-lg text-base leading-relaxed md:text-lg' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
              <Trans i18nKey='final_cta.description' ns='components/trial/lp/v4d' components={{ 1: <span style={{ color: NOPING_GREEN }} /> }} />
            </p>

            <div className='mb-6 flex items-center gap-8'>
              {[
                { val: NOPING_STATS.users, label: t('final_cta.stat_gamers') },
                { val: NOPING_STATS.servers, label: t('final_cta.stat_servers') },
                { val: NOPING_STATS.countries, label: t('final_cta.stat_countries') },
              ].map((s, i) => (
                <div key={i}>
                  <p className='text-lg font-bold' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-bai)' }}>
                    {s.val}
                  </p>
                  <p className='text-[10px] uppercase tracking-wider' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                    {s.label}
                  </p>
                </div>
              ))}
            </div>

            <p className='text-sm italic' style={{ color: `${MUTED_GRAY}A0`, fontFamily: 'var(--font-outfit)' }}>
              {t('final_cta.quote')}
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.15, ease: EASE_OUT }} viewport={{ once: true }}>
            <ThemedCard className='p-6 md:p-8' glow={NOPING_GREEN} opaque>
              <div className='mb-5 text-center'>
                <h3 className='mb-1 text-xl font-bold md:text-2xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }}>
                  {t('hero.form_title')}
                </h3>
                <p className='text-sm' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                  {t('hero.form_subtitle')}
                </p>
              </div>
              <CreateAccountV2 daysTrial={daysTrial} customTrialData={{ custom: trialConfigId, trialPageId }} isDisabledButton={isDisabledButton} formId='v4d-final-form' accentColor={NOPING_GREEN} />

              <div className='mt-5 grid grid-cols-3 gap-3 pt-5' style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                {[
                  { val: t('final_cta.stat_ping_value'), lbl: t('final_cta.stat_ping_label') },
                  { val: NOPING_STATS.servers, lbl: t('final_cta.stat_servers') },
                  { val: NOPING_STATS.users, lbl: t('final_cta.stat_gamers') },
                ].map((s) => (
                  <div key={s.lbl} className='text-center'>
                    <p className='text-sm font-bold' style={{ background: `linear-gradient(135deg, ${NOPING_GREEN}, ${ACCENT_BLUE})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                      {s.val}
                    </p>
                    <p className='mt-0.5 text-[10px]' style={{ color: MUTED_GRAY }}>
                      {s.lbl}
                    </p>
                  </div>
                ))}
              </div>
            </ThemedCard>
          </motion.div>
        </div>
      </section>

      <Footer text={t('footer', { year: new Date().getFullYear() })} />
    </div>
  );
}
